/**
 * RememberedFieldsPopover - Popover for configuring which form fields are remembered.
 *
 * Trigger: small cogwheel IconButton.
 * Dropdown: checkbox per configurable field + "Reset to Defaults" action.
 * Visual style follows ColumnVisibilityDropdown.
 */
import { useClickAway } from '@uidotdev/usehooks';
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { useCallback, useRef, useState } from 'react';
import { FaCheck } from 'react-icons/fa';
import { IoSettingsSharp } from 'react-icons/io5';

import { CONFIGURABLE_FIELDS, type RememberedFieldKey } from '../hooks/useRememberedFields';

import { calculateDropdownPosition } from '@/hooks/useDropdownPosition';

// ============================================================================
// Types
// ============================================================================

export interface RememberedFieldsPopoverProps {
  rememberedFields: RememberedFieldKey[];
  onToggle: (key: RememberedFieldKey) => void;
  onReset: () => void;
}

// ============================================================================
// Component
// ============================================================================

export default function RememberedFieldsPopover({ rememberedFields, onToggle, onReset }: RememberedFieldsPopoverProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const [flipUp, setFlipUp] = useState(false);
  const dropdownRef = useClickAway<HTMLDivElement>(() => setIsOpen(false));
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleToggle = useCallback(() => {
    if (!isOpen && triggerRef.current) {
      const pos = calculateDropdownPosition(triggerRef.current, {
        placement: 'bottom-end',
        estimatedHeight: 420,
        estimatedWidth: 192,
      });
      setFlipUp(pos.placement === 'top');
    }
    setIsOpen((prev) => !prev);
  }, [isOpen]);

  return (
    <div ref={dropdownRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggle}
        className={clsx(
          'flex h-6 w-6 items-center justify-center rounded-md transition-colors',
          isOpen ? 'bg-zinc-700 text-zinc-100' : 'text-ash hover:bg-zinc-800 hover:text-zinc-200',
        )}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label="Configure remembered fields"
      >
        <IoSettingsSharp className="h-3.5 w-3.5" />
      </button>

      {isOpen && (
        <div
          className={clsx(
            'absolute right-0 z-50 w-48 rounded-lg border border-zinc-700 bg-zinc-800 py-1 shadow-xl',
            flipUp ? 'bottom-full mb-1' : 'top-full mt-1',
          )}
          role="menu"
        >
          <div className="px-3 py-2 text-xs font-medium tracking-wider text-zinc-500 uppercase">Remember Fields</div>

          {CONFIGURABLE_FIELDS.map((field) => {
            const isActive = rememberedFields.includes(field.key);
            return (
              <button
                key={field.key}
                type="button"
                role="menuitemcheckbox"
                aria-checked={isActive}
                onClick={() => onToggle(field.key)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors hover:bg-zinc-700/50"
              >
                <span
                  className={clsx(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors',
                    isActive ? 'border-aether bg-aether text-zinc-900' : 'border-zinc-600 bg-transparent',
                  )}
                >
                  {isActive && <FaCheck className="h-2.5 w-2.5" />}
                </span>
                <span className={isActive ? 'text-zinc-100' : 'text-zinc-400'}>{field.label}</span>
              </button>
            );
          })}

          <div className="my-1 border-t border-zinc-700" />
          <button
            type="button"
            onClick={onReset}
            className="w-full px-3 py-1.5 text-left text-xs text-zinc-500 hover:bg-zinc-700/50 hover:text-zinc-300"
          >
            Reset to Defaults
          </button>
        </div>
      )}
    </div>
  );
}
